import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { foodTruckInfo } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'Menu', path: '/menu' },
    { name: 'Emplacements', path: '/emplacements' },
    { name: 'Avis', path: '/avis' },
    { name: 'À propos', path: '/a-propos' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Backdrop overlay when mobile menu is open */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-[#0E0E0E]/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isMobileMenuOpen
            ? 'bg-[#0E0E0E]/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-12 h-12 bg-[#F7C948] rounded-xl flex items-center justify-center transform group-hover:scale-105 transition-transform duration-300">
              <span className="font-display text-2xl font-bold text-[#0E0E0E]">JD</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="font-display text-xl font-bold text-white group-hover:text-[#F7C948] transition-colors duration-300">
                Just'Une Dalle
              </h1>
              <p className="text-xs text-gray-400">Food Truck</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'bg-[#F7C948] text-[#0E0E0E]'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center gap-2 bg-[#F7C948] text-[#0E0E0E] px-5 py-2.5 rounded-xl font-semibold hover:bg-[#FFE17D] transition-all duration-300 hover:shadow-lg hover:shadow-[#F7C948]/25"
            >
              <Phone size={18} />
              <span className="hidden xl:inline">Appeler</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors duration-300"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-[#0E0E0E]/98 backdrop-blur-lg border-t border-white/10 transition-all duration-300 ${
          isMobileMenuOpen
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-4'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 py-6 space-y-2">
          {navLinks.map((link, index) => (
            <Link
              key={link.path}
              to={link.path}
              className={`block px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                isActive(link.path)
                  ? 'bg-[#F7C948] text-[#0E0E0E]'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {link.name}
            </Link>
          ))}
          <div className="pt-4 space-y-3">
            <a
              href={`tel:${foodTruckInfo.phone.replace(/\s/g, '')}`}
              className="flex items-center justify-center gap-2 bg-[#F7C948] text-[#0E0E0E] px-6 py-3 rounded-xl font-semibold"
            >
              <Phone size={18} />
              Appeler maintenant
            </a>
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
              <MapPin size={16} />
              {foodTruckInfo.address}
            </div>
          </div>
        </nav>
      </div>
    </header>
    </>
  );
};

export default Header;
